import React, { useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  label: string;
  options: ComboboxOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  error?: string;
  /** Shown in the dropdown when nothing matches what was typed. */
  renderEmpty?: (query: string) => React.ReactNode;
}

/** Type-to-search single select (Figma "Brand you bought from"), with full keyboard support. */
const Combobox: React.FC<ComboboxProps> = ({ label, options, value, onChange, placeholder, error, renderEmpty }) => {
  const id = useId();
  const listId = `${id}-list`;
  const selected = options.find((o) => o.value === value);
  const [query, setQuery] = useState(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Showing the chosen brand's own name shouldn't filter the list down to it.
    if (!q || q === selected?.label.toLowerCase()) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, selected]);

  const choose = (option: ComboboxOption) => {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (matches.length) setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length);
    } else if (e.key === 'Enter' && open && matches[active]) {
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div
      ref={root}
      className="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-2"
      // Close when focus leaves the whole control (not when moving into the dropdown).
      onBlur={(e) => {
        if (!root.current?.contains(e.relatedTarget as Node)) {
          setOpen(false);
          setQuery(selected?.label ?? query);
        }
      }}
    >
      <label htmlFor={id} className="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary">
        {label}
      </label>
      <div
        className={cn(
          'tw-flex tw-h-[52px] tw-w-full tw-items-center tw-gap-2.5 tw-rounded-xl tw-border tw-bg-ds-surface tw-px-[14px]',
          error
            ? 'tw-border-ds-danger-fg tw-shadow-[inset_0_0_0_0.5px_var(--ds-danger-fg)]'
            : open
              ? 'tw-border-ds-brand tw-shadow-[inset_0_0_0_0.5px_var(--ds-brand-primary)]'
              : 'tw-border-ds-border-strong',
        )}
      >
        <Icon icon={Search} className="tw-text-ds-text-tertiary" />
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${listId}-${matches[active].value}` : undefined}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
            if (value) onChange(undefined);
          }}
          onKeyDown={onKeyDown}
          className="tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[15px] tw-leading-5 tw-text-ds-text-primary tw-outline-none placeholder:tw-text-ds-text-tertiary"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? 'Close list' : 'Open list'}
          onClick={() => setOpen((o) => !o)}
          className="tw-text-ds-text-secondary"
        >
          <Icon icon={ChevronDown} className={cn('tw-transition-transform', open && 'tw-rotate-180')} />
        </button>
      </div>
      {error && (
        <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-[12px] tw-font-medium tw-leading-4 tw-text-ds-danger-fg">
          <Icon icon={AlertCircle} size={14} className="tw-mt-px" />
          {error}
        </p>
      )}
      {open && (
        <div className="tw-absolute tw-inset-x-0 tw-top-full tw-z-20 tw-mt-2 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-shadow-ds-modal">
          {matches.length ? (
            <ul id={listId} role="listbox" aria-label={label} className="tw-max-h-64 tw-overflow-y-auto tw-p-1.5">
              {matches.map((option, i) => (
                <li
                  key={option.value}
                  id={`${listId}-${option.value}`}
                  role="option"
                  aria-selected={option.value === value}
                  // Keep focus on the input so the blur handler doesn't close the list first.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(option)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'tw-cursor-pointer tw-rounded-[10px] tw-px-3 tw-py-2.5 tw-text-[15px] tw-leading-5 tw-text-ds-text-primary',
                    i === active && 'tw-bg-ds-subtle',
                    option.value === value && 'tw-font-semibold',
                  )}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          ) : (
            <div id={listId} role="status" className="tw-p-4">
              {renderEmpty?.(query.trim())}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Combobox;
